import { useCallback, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { HeaderBox, PageContainer } from './styled';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import { ScoringType } from './types/scoringType';
import ScoringTypesTable from './ScoringTypesTable';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import scoringTypes from '../../apis/scoringTypes';
import { RootState } from '../../redux/store';
import { useSelector } from 'react-redux';
import AddScoringTypeModal from './AddScoringTypeModal';
import EditScoringTypeModal from './EditScoringTypeModal';
import { StyledButton } from '../Exams/styled';
import Icons from '../../components/icons';

const ScoringTypesContainer = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [scoringTypeData, setScoringTypeData] = useState<ScoringType[]>();
  const [selectedScoringType, setSelectedScoringType] =
    useState<ScoringType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  //  SnackBar
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await scoringTypes.getScoringTypes(loggedInUser.school);
      setScoringTypeData(response as any);
    } catch (error) {
      console.error(error);
      showToast('Failed to load scoring type.', SnackbarSeverityType.error);
    } finally {
      setIsLoading(false);
    }
  }, [loggedInUser.school]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const handleEditOpenModal = (scoringType: ScoringType) => {
    setSelectedScoringType(scoringType);
    setIsEditModalOpen(true);
  };

  const handleEditCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedScoringType(null);
  };

  const handleDeleteClick = (scoringType: ScoringType) => {
    setSelectedScoringType(scoringType);
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedScoringType(null);
  };

  const handleApiSuccessAndFailure = (isSuccess: boolean, message: string) => {
    setIsAddModalOpen(false);
    handleEditCloseModal();
    showToast(
      message,
      isSuccess ? SnackbarSeverityType.success : SnackbarSeverityType.error
    );
  };

  const handleDeleteConfirm = async () => {
    if (selectedScoringType == null || !selectedScoringType.id) {
      showToast(
        'No scoring type selected for deletion.',
        SnackbarSeverityType.error
      );
      handleDeleteCancel();
      return;
    }
    setIsDeleting(true);
    try {
      const response = await scoringTypes.deleteScoringType(
        selectedScoringType.id
      );
      if (response) {
        setScoringTypeData((prev) =>
          prev?.filter(
            (item: ScoringType) => item.id !== selectedScoringType.id
          )
        );

        showToast(
          `Scoring type "${selectedScoringType.name}" deleted successfully`,
          SnackbarSeverityType.success
        );
      }
    } catch (error) {
      showToast(
        `Scoring type associated with an exam cannot be deleted"`,
        SnackbarSeverityType.error
      );
    }

    setIsDeleting(false);

    handleDeleteCancel();
  };

  return (
    <>
      <PageContainer>
        <HeaderBox>
          <Typography variant='h4' fontWeight='bold'>
            Scoring Types
          </Typography>
          <StyledButton
            variant='contained'
            onClick={() => setIsAddModalOpen(true)}
          >
            <Icons.PlusWhite style={{ marginRight: '6px' }} /> Add Scoring Type
          </StyledButton>
        </HeaderBox>

        <ScoringTypesTable
          isLoading={isLoading}
          scoringTypes={scoringTypeData || []}
          onEdit={(scoringType) => handleEditOpenModal(scoringType)}
          onDelete={handleDeleteClick}
        />

        <AddScoringTypeModal
          fetchData={fetchData}
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onApiSuccessAndFailure={handleApiSuccessAndFailure}
        />

        <EditScoringTypeModal
          fetchData={fetchData}
          open={isEditModalOpen}
          onClose={handleEditCloseModal}
          scoringType={selectedScoringType}
          onApiSuccessAndFailure={handleApiSuccessAndFailure}
        />

        <ConfirmationDialog
          open={isDeleteModalOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          message={`Are you sure you want to delete "${
            selectedScoringType?.name || ''
          }"?`}
          disable={isDeleting}
        />
      </PageContainer>
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default ScoringTypesContainer;
